import { EDITABLE_ZONE_KINDS, type EditableZoneKind, type ZoneBatchRequest, type ZoneRow, isDerivedZone } from "../../../../api/rest";

export type Ring = number[][];
export interface EditableZone {
  id: string;
  kind: EditableZoneKind;
  polygon: Ring;
  maxSpeedMps: number | null;
  directionRad: number | null;
}
type P = readonly [number, number];

export const SNAP_M = 0.5;
export const snap = (v: number, on: boolean): number => (on ? Math.round(v / SNAP_M) * SNAP_M : v);

export function rectToRing(a: P, b: P): Ring {
  const x0 = Math.min(a[0], b[0]), x1 = Math.max(a[0], b[0]);
  const y0 = Math.min(a[1], b[1]), y1 = Math.max(a[1], b[1]);
  return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
}

export function ringArea(r: Ring): number {
  let a = 0;
  for (let i = 0, n = r.length; i < n; i++) {
    const [px, py] = r[i], [qx, qy] = r[(i + 1) % n];
    a += px * qy - qx * py;
  }
  return Math.abs(a / 2);
}

const cross = (o: P | number[], a: P | number[], b: P | number[]) =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

function segmentsCross(a: number[], b: number[], c: number[], d: number[]): boolean {
  const d1 = cross(c, d, a), d2 = cross(c, d, b), d3 = cross(a, b, c), d4 = cross(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/**
 * Detects proper crossings only. Touching vertices and collinear overlaps pass.
 * Intentional: matches the backend gate ZoneBatchService.selfIntersects, and the two must agree.
 */
export function selfIntersects(r: Ring): boolean {
  const n = r.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsCross(r[i], r[(i + 1) % n], r[j], r[(j + 1) % n])) return true;
    }
  }
  return false;
}

/**
 * The server's ring gate (ZoneBatchService.normalizeRing + validateRing), client-side:
 * consecutive and closing duplicates dropped, then at least 3 points, non-zero area and
 * no proper self-crossing. The editor never keeps a ring this refuses.
 */
export function ringValid(r: Ring): boolean {
  const same = (a: number[], b: number[]) => Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
  const out: Ring = [];
  for (const p of r) {
    if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) return false;
    if (out.length === 0 || !same(out[out.length - 1], p)) out.push(p);
  }
  if (out.length > 1 && same(out[0], out[out.length - 1])) out.pop();
  return out.length >= 3 && ringArea(out) >= 1e-6 && !selfIntersects(out);
}

/** Even-odd ray cast. */
export function pointInPolygon(p: P, r: Ring): boolean {
  let inside = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i], [xj, yj] = r[j];
    const crosses = (yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

export function nearestVertex(p: P, r: Ring, tolM: number): number | null {
  let best = -1, bestD = tolM * tolM;
  r.forEach(([x, y], i) => {
    const d = (x - p[0]) ** 2 + (y - p[1]) ** 2;
    if (d <= bestD) { bestD = d; best = i; }
  });
  return best < 0 ? null : best;
}

export function nearestEdge(p: P, r: Ring, tolM: number): { index: number; point: [number, number] } | null {
  let best: { index: number; point: [number, number] } | null = null, bestD = tolM * tolM;
  for (let i = 0, n = r.length; i < n; i++) {
    const [ax, ay] = r[i], [bx, by] = r[(i + 1) % n];
    const vx = bx - ax, vy = by - ay, len2 = vx * vx + vy * vy || 1e-12;
    const t = Math.max(0, Math.min(1, ((p[0] - ax) * vx + (p[1] - ay) * vy) / len2));
    const qx = ax + t * vx, qy = ay + t * vy;
    const d = (qx - p[0]) ** 2 + (qy - p[1]) ** 2;
    if (d <= bestD) { bestD = d; best = { index: i, point: [qx, qy] }; }
  }
  return best;
}

export function centroid(r: Ring): [number, number] {
  const n = r.length || 1;
  return [r.reduce((s, [x]) => s + x, 0) / n, r.reduce((s, [, y]) => s + y, 0) / n];
}

export const translateRing = (r: Ring, dx: number, dy: number): Ring => r.map(([x, y]) => [x + dx, y + dy]);

const ringEquals = (a: Ring, b: Ring) =>
  a.length === b.length && a.every((p, i) => p[0] === b[i][0] && p[1] === b[i][1]);

export function zoneEquals(a: EditableZone, b: EditableZone): boolean {
  return a.id === b.id && a.kind === b.kind && a.maxSpeedMps === b.maxSpeedMps
    && a.directionRad === b.directionRad && ringEquals(a.polygon, b.polygon);
}

const isEditableKind = (k: string): k is EditableZoneKind => (EDITABLE_ZONE_KINDS as readonly string[]).includes(k);

export function toEditable(row: ZoneRow): EditableZone | null {
  if (isDerivedZone(row.id) || row.nodeId != null || !row.polygon || !isEditableKind(row.kind)) return null;
  return { id: row.id, kind: row.kind, polygon: row.polygon.map(([x, y]) => [x, y]),
           maxSpeedMps: row.maxSpeedMps, directionRad: row.directionRad };
}

export function diffZones(original: EditableZone[], working: EditableZone[]): ZoneBatchRequest {
  const before = new Map(original.map((z) => [z.id, z]));
  const after = new Map(working.map((z) => [z.id, z]));
  const upsert = working
    .filter((w) => { const o = before.get(w.id); return !o || !zoneEquals(o, w); })
    .map((w) => ({ id: w.id, kind: w.kind, polygon: w.polygon, maxSpeedMps: w.maxSpeedMps, directionRad: w.directionRad }));
  const del = original.filter((o) => !after.has(o.id)).map((o) => o.id);
  return { upsert, delete: del };
}

const MAX_RANDOM_ATTEMPTS = 64;

export function mintZoneId(kind: EditableZoneKind, taken: ReadonlySet<string>, rnd: () => number = Math.random): string {
  let hex = "000000";
  for (let attempt = 0; attempt < MAX_RANDOM_ATTEMPTS; attempt++) {
    hex = Math.floor(rnd() * 0xffffff).toString(16).padStart(6, "0");
    const id = `${kind}-${hex}`;
    if (!taken.has(id)) return id;
  }
  for (let n = 1; ; n++) {
    const id = `${kind}-${hex}-${n}`;
    if (!taken.has(id)) return id;
  }
}
